import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit, Visibility } from "@mui/icons-material";
import {
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import TaskVeiwController from "../../redux/actions/TaskApiAction";
import AddTaskFormModal from "./AddTaskForm";
import TaskView from "./ViewTask";
import TaskMobileView from "./TaskMobileView";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import StatusReasonModal from "./StatusReasonModal";
import ExportButton from "../../components/ag grid/ExportButton";

import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx, BRAND } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const EXPORT_COLUMNS = ["title", "priority", "status", "created_at"];

const statusOptions = [
  "New",
  "Pending",
  "In Progress",
  "Ready for Testing",
  "Testing",
  "Changes Required",
  "Reopened",
  "Completed",
  "Blocked"
];
const priorityOptions = ["Low", "Medium", "High", "Critical"];

const statusColors = {
  New: "#2196F3",
  Pending: "#FF9800",
  "In Progress": "#9C27B0",
  "Ready for Testing": "#00BCD4",
  Testing: "#3F51B5",
  "Changes Required": "#FF5722",
  Reopened: "#FFC107",
  Completed: "#4CAF50",
  Blocked: "#F44336",
};

const priorityColors = {
  Low: "#8BC34A",
  Medium: "#FFC107",
  High: "#FF5722",
  Critical: "#F44336",
};

const GridSelect = ({ value, options, colorMap, onChange }) => (
  <Select
    value={value}
    onChange={onChange}
    sx={{
      width: "100%",
      height: 32,
      color: colorMap[value] || "#374151",
      fontWeight: 600,
      fontSize: "0.8125rem",
      bgcolor: "#fff",
      borderRadius: "8px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E5E7EB",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: BRAND.orange || "#F97316",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: BRAND.orange || "#F97316",
      }
    }}
  >
    {options.map((option) => (
      <MenuItem key={option} value={option} sx={{ color: colorMap[option] }}>
        {option}
      </MenuItem>
    ))}
  </Select>
);

const TaskList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const employeeMode = location.pathname.includes("/employee_task_issue");
  const { hasPermission } = usePermission();

  const permissionModule = employeeMode
    ? "employee_task_issue"
    : "task_issues_management";

  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [tabValue, setTabValue] = useState("Task");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  
  const [statusReasonData, setStatusReasonData] = useState({ open: false, taskId: null, newStatus: "" });
  const [deleteModalData, setDeleteModalData] = useState({ open: false, taskId: null });
  const [pageSize, setPageSize] = useState(10);
  
  const [filterPriority, setFilterPriority] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const { getProjects } = ProjectViewController();
  const { projects } = useSelector((state) => state.project);
  const { getTasks, updateTask, deleteTask } = TaskVeiwController();
  const { tasks } = useSelector((state) => state.task);

  const filteredTasks = tasks || [];

  const canCreate = hasPermission(permissionModule, "create");
  const canEdit = hasPermission(permissionModule, "edit");
  const canDelete = hasPermission(permissionModule, "delete");
  const canExport = hasPermission(permissionModule, "export");

  useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  useEffect(() => {
    if (projects?.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      dispatch(getTasks(tabValue, selectedProjectId, filterPriority, filterStatus, showDeleted));
    }
  }, [dispatch, tabValue, selectedProjectId, filterPriority, filterStatus, showDeleted]);

  const selectedProject =
    projects?.find((p) => p.id === selectedProjectId) || {};

  const handleDelete = useCallback(
    (taskId) => setDeleteModalData({ open: true, taskId }),
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteModalData.taskId) {
      dispatch(deleteTask(deleteModalData.taskId));
    }
    setDeleteModalData({ open: false, taskId: null });
  }, [dispatch, deleteTask, deleteModalData.taskId]);

  const handleEdit = useCallback((task) => {
    setEditTask(task);
    setShowModal(true);
  }, []);

  const handleView = useCallback((task) => {
    setSelectedTask(task);
    setShowViewModal(true);
  }, []);

  const handleStatusChange = useCallback(
    (taskId, value) => {
      const requiresReason = ["Blocked", "Reopened", "Changes Required"].includes(value);
      if (requiresReason) {
        setStatusReasonData({ open: true, taskId, newStatus: value });
      } else {
        dispatch(updateTask(taskId, { status: value }, tabValue, selectedProjectId));
      }
    },
    [dispatch, updateTask, tabValue, selectedProjectId]
  );

  const handleStatusReasonSubmit = (reason) => {
    if (statusReasonData.taskId) {
      dispatch(updateTask(statusReasonData.taskId, { status: statusReasonData.newStatus, status_reason: reason }, tabValue, selectedProjectId));
    }
    setStatusReasonData({ open: false, taskId: null, newStatus: "" });
  };

  const handlePriorityChange = useCallback(
    (taskId, value) => {
      dispatch(updateTask(taskId, { priority: value }, tabValue, selectedProjectId));
    },
    [dispatch, updateTask, tabValue, selectedProjectId]
  );

  const handleSave = useCallback(
    (projectId, category) => {
      dispatch(getTasks(category, projectId));
    },
    [dispatch, getTasks]
  );

  const openAddModal = useCallback(() => {
    setEditTask(null);
    setShowModal(true);
  }, []);

  const handleToggleSelect = useCallback((task) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === task.id);
      if (exists) return prev.filter((row) => row.id !== task.id);
      return [...prev, task];
    });
  }, []);

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) return;
    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      EXPORT_COLUMNS.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "task_list.xlsx");
  }, [selectedRows]);

  const handleProjectChange = useCallback((projectId) => {
    setSelectedProjectId(Number(projectId));
    setSelectedRows([]);
  }, []);

  const handleTabChange = useCallback((value) => {
    setTabValue(value);
    setSelectedRows([]);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Title",
        field: "title",
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Assigned To",
        flex: 1,
        minWidth: 140,
        valueGetter: (params) => params.data.assigned_to?.name || "—",
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Created On",
        flex: 1,
        minWidth: 120,
        valueGetter: (params) =>
          params.data.created_at
            ? new Date(params.data.created_at).toLocaleDateString()
            : "—",
      },
      {
        headerName: "Priority",
        field: "priority",
        width: 130,
        maxWidth: 140,
        minWidth: 130,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <GridSelect
            value={params.value}
            options={priorityOptions}
            colorMap={priorityColors}
            onChange={(e) =>
              handlePriorityChange(params.data.id, e.target.value)
            }
          />
        ),
      },
      {
        headerName: "Status",
        field: "status",
        width: 140,
        maxWidth: 150,
        minWidth: 140,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <GridSelect
            value={params.value}
            options={statusOptions}
            colorMap={statusColors}
            onChange={(e) => handleStatusChange(params.data.id, e.target.value)}
          />
        ),
      },
      {
        headerName: "Actions",
        pinned: "right",
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleView(params.data)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {!params.data.is_deleted && canEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(params.data)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!params.data.is_deleted && canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "#d32f2f" }}
                  onClick={() => handleDelete(params.data.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [
      canEdit,
      canDelete,
      handleView,
      handleEdit,
      handleDelete,
      handlePriorityChange,
      handleStatusChange,
    ]
  );

  const projectFilter = (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="task-project-label">Project</InputLabel>
      <Select
        labelId="task-project-label"
        value={selectedProjectId || ""}
        label="Project"
        onChange={(e) => setSelectedProjectId(Number(e.target.value))}
      >
        <MenuItem value="" disabled>
          Select Project
        </MenuItem>
        {projects?.map((project) => (
          <MenuItem key={project.id} value={project.id}>
            {project.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {isMobile ? (
        <TaskMobileView
          tabValue={tabValue}
          onTabChange={handleTabChange}
          projects={projects || []}
          selectedProjectId={selectedProjectId}
          onProjectChange={handleProjectChange}
          tasks={tasks || []}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          canExport={canExport}
          employeeMode={employeeMode}
        />
      ) : (
      <ListPageLayout
        title="Tasks & Issues"
        headerAction={
          canCreate ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              disabled={!selectedProjectId}
              onClick={openAddModal}
              sx={toolbarButtonSx}
            >
              Add Task/Issue
            </Button>
          ) : null
        }
        toolbarLeft={projectFilter}
        toolbarRight={
          <>
            <FormControl sx={{ minWidth: 120, bgcolor: "#fff", borderRadius: "12px", height: 48 }}>
              <InputLabel id="filter-priority-label" sx={{ lineHeight: '48px', top: '-14px', '&.MuiInputLabel-shrink': { top: 0 } }}>Priority</InputLabel>
              <Select
                labelId="filter-priority-label"
                value={filterPriority}
                label="Priority"
                onChange={(e) => setFilterPriority(e.target.value)}
                sx={{ height: 48, borderRadius: "12px" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120, bgcolor: "#fff", borderRadius: "12px", height: 48 }}>
              <InputLabel id="filter-status-label" sx={{ lineHeight: '48px', top: '-14px', '&.MuiInputLabel-shrink': { top: 0 } }}>Status</InputLabel>
              <Select
                labelId="filter-status-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ height: 48, borderRadius: "12px" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Ready for Testing">Ready for Testing</MenuItem>
                <MenuItem value="Testing">Testing</MenuItem>
                <MenuItem value="Changes Required">Changes Required</MenuItem>
                <MenuItem value="Reopened">Reopened</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: BRAND.navy }}>
                  Show Deleted
                </Typography>
              }
              sx={{ ml: 1, mr: 1 }}
            />

            {canExport && (
              <ExportButton
                gridRef={gridRef}
                fileName="task_list.xlsx"
                exportColumns={["title", "priority", "status", "created_at"]}
                disabled={selectedRows.length === 0}
              />
            )}
          </>
        }
        tabs={
          <Tabs
            value={tabValue}
            onChange={(_e, value) => {
              setTabValue(value);
              setFilterPriority("");
              setFilterStatus("");
            }}
            className="list-page-tabs"
          >
            <Tab label="Assigned Task" value="Task" />
            <Tab label="Issue Tracker" value="Issue" />
          </Tabs>
        }
      >
        <div className="list-page-grid">
          <ReusableGrid
            rowData={filteredTasks}
            columnDefs={columnDefs}
            gridRef={gridRef}
            pagination={true}
            pageSize={pageSize}
            getRowId={(params) => String(params.data.id)}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            emptyMessage={
              selectedProjectId ? "No records found" : "Select a project first"
            }
            emptyHint={
              selectedProjectId
                ? canCreate
                  ? 'Click "Add Task/Issue" to create one.'
                  : "No tasks or issues for this project."
                : "Choose a project from the dropdown above."
            }
          />
        </div>
      </ListPageLayout>
      )}

      {showModal && (
        <AddTaskFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editTask}
          selectedProject={selectedProject}
          category={tabValue}
          projectId={selectedProjectId}
          onSave={handleSave}
        />
      )}

      <TaskView
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        selectedTask={selectedTask}
        category={tabValue}
      />

      <StatusReasonModal
        open={statusReasonData.open}
        handleClose={() => setStatusReasonData({ open: false, taskId: null, newStatus: "" })}
        handleSubmit={handleStatusReasonSubmit}
        newStatus={statusReasonData.newStatus}
      />

      <Dialog 
        open={deleteModalData.open} 
        onClose={() => setDeleteModalData({ open: false, taskId: null })}
        PaperProps={{
          style: { borderRadius: "12px", padding: "8px" }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: BRAND.primary }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this task/issue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalData({ open: false, taskId: null })} sx={{ color: BRAND.text }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskList;
